import PropTypes from "prop-types"
import * as React from "react"
import * as yup from "yup"
import { useFormik } from "formik"

import CTAFormWrapper from "components/CTAForm/CTAFormWrapper"
import CTAFormContainer from "components/CTAForm/CTAFormContainer"
import CTAFormContent from "components/CTAForm/CTAFormContent"
import CTAFormSideBar from "components/CTAForm/CTAFormSideBar"
import CTAFormClientTitle from "components/CTAForm/CTAFormClientTitle"
import CTAFormClientMessage from "components/CTAForm/CTAFormClientMessage"
import CTAFormClientName from "components/CTAForm/CTAFormClientName"
import CTAFormClientPosition from "components/CTAForm/CTAFormClientPosition"
import CTAFormTitle from "components/CTAForm/CTAFormTitle"
import CTAFormFields from "components/CTAForm/CTAFormFields"
import CTAFormField from "components/CTAForm/CTAFormField"
import CTAFormBottom from "components/CTAForm/CTAFormBottom"
import CTAFormConfirmation from "components/CTAForm/CTAFormConfirmation"
import CTAFormLink from "components/CTAForm/CTAFormLink"
import CTAFormSpinner from "components/CTAForm/CTAFormSpinner"
import CTAFormSpinnerWrapper from "components/CTAForm/CTAFormSpinnerWrapper"
import CTAFormFinalBtn from "components/CTAForm/CTAFormFinalBtn"
import CTAFormFinalText from "components/CTAForm/CTAFormFinalText"
import CTAFormFinalWrapper from "components/CTAForm/CTAFormFinalWrapper"
import CTAFormFormFieldWrapper from "components/CTAForm/CTAFormFormFieldWrapper"
import CTAFormFormFieldName from "components/CTAForm/CTAFormFormFieldName"
import { CTA_FORM_LINK } from "utils/formLinks"
import FormFinalImage from "components/Icons/FormFinalImage"
import htmlFormatting from "utils/htmlFormatting"
import pushGTMEvent from "utils/pushGTMEvent"
import Button from "components/Buttons/Button"
import DEFAULT_FORM_ERROR from "utils/defaultFormError"
import { handleReCaptchaVerify } from "utils/handleReCaptchaVerify"
import { MAX_INPUT_LENGTH, MAX_TEXTAREA_LENGTH } from "utils/inputLimits"
import { noHTMLTest } from "utils/noHTMLRegexp"
import { isTemporaryEmail, isNonBusinessEmail } from "utils/temporaryEmailsList"
import useUserId from "../hooks/useUserId"

const validationSchema = yup.object({
  name: yup
    .string("Full Name")
    .required("Name is required")
    .test(
      "no-html",
      "Name should not contain HTML tags or other disallowed characters",
      noHTMLTest,
    ),
  email: yup
    .string("Email")
    .email("Enter a valid email")
    .required("Email is required")
    .test(
      "no-temp-email",
      "Temporary email addresses are not allowed",
      (value) => !isTemporaryEmail(value),
    )
    .test(
      "no-non-business-email",
      "Non-business email addresses are not allowed",
      (value) => !isNonBusinessEmail(value),
    )
    .test(
      "no-html",
      "Name should not contain HTML tags or other disallowed characters",
      noHTMLTest,
    ),
  message: yup
    .string("About project")
    .required("Message is required")
    .test(
      "no-html",
      "Name should not contain HTML tags or other disallowed characters",
      noHTMLTest,
    ),
})

const CTAForm = ({
  title,
  titleTag,
  formId,
  clientTitle,
  clientMessage,
  clientName,
  clientPosition,
  buttonText,
  topPartTheme,
  bottomPartTheme,
  confirmationText,
  confirmationLinkText,
  confirmationLink,
  formFieldNameTitle,
  formFieldEmailTitle,
  formFieldMessageTitle,
  formPlaceholderEmail,
  formPlaceholderName,
  formPlaceholderMessage,
  finalStageBtnText,
  finalStageText,
  recaptcha,
  sectionId,
  isBlog,
}) => {
  const userID = useUserId()

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      userID,
      formId,
      name: "",
      email: "",
      message: "",
    },
    isInitialValid: false,
    validationSchema,
  })

  const [prevFormState, setPrevFormState] = React.useState(formik.initialValues)
  const [sending, setSending] = React.useState(false)
  const [showFinal, setShowFinal] = React.useState(false)
  const [formHasError, setFormHasError] = React.useState(false)
  const [recaptchaRating, setRecaptchaRating] = React.useState(null)

  const showFinalHandler = () => {
    setShowFinal(!showFinal)
  }

  return (
    <CTAFormWrapper
      isBlog={isBlog}
      id={sectionId}
      topPartTheme={topPartTheme}
      bottomPartTheme={bottomPartTheme}
    >
      <CTAFormContainer isBlog={isBlog}>
        <CTAFormContent
          isBlog={isBlog}
          id="ctaForm"
          formName="CTA"
          recaptchaValue={recaptchaRating}
          formValues={formik.values}
          preSubmit={() => handleReCaptchaVerify(recaptcha, setRecaptchaRating)}
          setSending={setSending}
          fieldMapping={{
            name: "firstname",
            lastname: "lastname",
            message: "message",
            email: "email",
            userID: "userID",
            recaptchaValue: "recaptchaValue",
          }}
          endpoint={CTA_FORM_LINK}
          postSubmit={(success) => {
            if (success) {
              formik.resetForm()
              setFormHasError(false)
              showFinalHandler()
            } else {
              formik.resetForm()
              setFormHasError(true)
              showFinalHandler()
            }
          }}
        >
          <CTAFormTitle isBlog={isBlog} as={titleTag}>
            {title}
          </CTAFormTitle>
          <CTAFormFields isBlog={isBlog}>
            <CTAFormField type="hidden" key="formId" name="formId" />
            <CTAFormFormFieldWrapper isBlog={isBlog}>
              <CTAFormFormFieldName isBlog={isBlog}>
                {(formFieldNameTitle && `${formFieldNameTitle}`) || "Full name "}
                <span>*</span>
              </CTAFormFormFieldName>
              <CTAFormField
                isBlog={isBlog}
                error={formik.errors.name}
                onChange={formik.handleChange}
                value={formik.values.name}
                touched={formik.touched?.name}
                placeholder={formPlaceholderName}
                maxLength={MAX_INPUT_LENGTH}
                type="text"
                key="name"
                name="name"
                required
                onFocus={() => {
                  formik.setFieldTouched("name", true, true)
                }}
                onBlur={() => {
                  if (formik.values.name !== "" && prevFormState.name !== formik.values.name)
                    setPrevFormState(formik.values)
                  if (
                    formik.values.name !== "" &&
                    !formik.errors.name &&
                    prevFormState.name !== formik.values.name
                  )
                    pushGTMEvent("CTAFormName", formik.values.name)
                }}
              />
            </CTAFormFormFieldWrapper>
            <CTAFormFormFieldWrapper isBlog={isBlog}>
              <CTAFormFormFieldName isBlog={isBlog}>
                {(formFieldEmailTitle && `${formFieldEmailTitle}`) || "Email "}
                <span>*</span>
              </CTAFormFormFieldName>
              <CTAFormField
                isBlog={isBlog}
                error={formik.errors.email}
                onChange={formik.handleChange}
                value={formik.values.email || ""}
                touched={formik.touched?.email}
                placeholder={formPlaceholderEmail}
                type="email"
                key="email"
                name="email"
                required
                onFocus={() => {
                  formik.setFieldTouched("email", true, true)
                }}
                onBlur={() => {
                  if (formik.values.email !== "" && prevFormState.email !== formik.values.email)
                    setPrevFormState(formik.values)
                  if (
                    formik.values.email !== "" &&
                    !formik.errors.email &&
                    prevFormState.email !== formik.values.email
                  )
                    pushGTMEvent("email_field_touched", "CTA")
                }}
              />
            </CTAFormFormFieldWrapper>
            <CTAFormFormFieldWrapper isBlog={isBlog}>
              <CTAFormFormFieldName isBlog={isBlog}>
                {(formFieldMessageTitle && `${formFieldMessageTitle}`) || "About project "}
                <span>*</span>
              </CTAFormFormFieldName>
              <CTAFormField
                isBlog={isBlog}
                as="textarea"
                error={formik.errors.message}
                onChange={formik.handleChange}
                value={formik.values.message || ""}
                touched={formik.touched?.message}
                placeholder={formPlaceholderMessage}
                type="text"
                key="message"
                name="message"
                maxLength={MAX_TEXTAREA_LENGTH}
                required
                rows="5"
                onFocus={() => {
                  formik.setFieldTouched("message", true, true)
                }}
                onBlur={() => {
                  if (
                    formik.values.message !== "" &&
                    prevFormState.message !== formik.values.message
                  )
                    setPrevFormState(formik.values)
                  if (
                    formik.values.message !== "" &&
                    !formik.errors.message &&
                    prevFormState.message !== formik.values.message
                  )
                    pushGTMEvent("CTAFormMessage", formik.values.message)
                }}
              />
            </CTAFormFormFieldWrapper>
          </CTAFormFields>
          <CTAFormBottom isBlog={isBlog}>
            <CTAFormConfirmation isBlog={isBlog}>
              {confirmationText}
              <CTAFormLink isBlog={isBlog} target="_blank" to={confirmationLink}>
                {confirmationLinkText}
              </CTAFormLink>
            </CTAFormConfirmation>

            <Button id="cta-form-submit" variant="contained" type="submit">
              {buttonText}
            </Button>
          </CTAFormBottom>
        </CTAFormContent>
        <CTAFormSideBar isBlog={isBlog}>
          <CTAFormClientTitle isBlog={isBlog}> {clientTitle}</CTAFormClientTitle>
          <CTAFormClientMessage isBlog={isBlog}> {clientMessage}</CTAFormClientMessage>
          <CTAFormClientName isBlog={isBlog}> {clientName}</CTAFormClientName>
          <CTAFormClientPosition isBlog={isBlog}> {clientPosition}</CTAFormClientPosition>
        </CTAFormSideBar>

        {sending && (
          <CTAFormSpinnerWrapper>
            <CTAFormSpinner />
          </CTAFormSpinnerWrapper>
        )}
        {showFinal && (
          <CTAFormFinalWrapper>
            {!formHasError && <FormFinalImage />}
            {formHasError ? (
              <CTAFormFinalText>{DEFAULT_FORM_ERROR}</CTAFormFinalText>
            ) : (
              finalStageText && (
                <CTAFormFinalText
                  dangerouslySetInnerHTML={{
                    __html: htmlFormatting(finalStageText?.childMarkdownRemark?.html),
                  }}
                />
              )
            )}
            <CTAFormFinalBtn onClick={showFinalHandler}>
              {finalStageBtnText || "Close"}
            </CTAFormFinalBtn>
          </CTAFormFinalWrapper>
        )}
      </CTAFormContainer>
    </CTAFormWrapper>
  )
}

CTAForm.propTypes = {
  title: PropTypes.string.isRequired,
  titleTag: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  clientTitle: PropTypes.string.isRequired,
  clientMessage: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  clientPosition: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  topPartTheme: PropTypes.string.isRequired,
  bottomPartTheme: PropTypes.string.isRequired,
  confirmationText: PropTypes.string.isRequired,
  confirmationLinkText: PropTypes.string.isRequired,
  confirmationLink: PropTypes.string.isRequired,
  formFieldNameTitle: PropTypes.string.isRequired,
  formFieldEmailTitle: PropTypes.string.isRequired,
  formFieldMessageTitle: PropTypes.string.isRequired,
  formPlaceholderEmail: PropTypes.string,
  formPlaceholderName: PropTypes.string,
  formPlaceholderMessage: PropTypes.string,
  finalStageText: PropTypes.object.isRequired,
  finalStageBtnText: PropTypes.string.isRequired,
  isBlog: PropTypes.bool.isRequired,
  recaptcha: PropTypes.object,
  sectionId: PropTypes.string,
}

CTAForm.defaultProps = {
  recaptcha: null,
  formPlaceholderEmail: null,
  formPlaceholderName: null,
  formPlaceholderMessage: null,
  sectionId: "",
}

export default CTAForm
